// Catalog.ID Client — Session State
// ============================================================================

import { api } from '../api/client';
import { eventSocket } from './event-socket';
import { clearActiveCid } from './device-storage';
import { rememberOrigin, type IdentityOrigin } from './origins';

export interface SessionInfo {
    cid: string;
    accountType: string;
    status: string;
    isOperator?: boolean;
    /**
     * Public session handle (`session.sid`) — a stable, opaque identifier for
     * the current session, decoupled from the auth token. Surfaced to the EFS
     * module via the `get-active-identity` hub mode; EFS records it as
     * `issuing_session_id` on wrap rows. Set from the server response on
     * login / registration and restored from `/api/account/me` on reload.
     */
    sessionId?: string;
}

class SessionState {
    private info: SessionInfo | null = null;

    /**
     * Initialize a session after login or registration.
     *
     * `origin` is where the session was created (docs/cid-governance.md D18),
     * when that is not the entry node — a delegate activation at the
     * organization's origin, an organization registered under a founder's
     * session at the founder's origin. Without it, an origin already on record
     * for the identity stands (a login resolves it first); an identity with none
     * lives at the entry node, where a personal registration creates it.
     */
    start(token: string, info: SessionInfo, origin?: IdentityOrigin | null): void {
        if (origin) rememberOrigin(info.cid, origin);
        api.setToken(token, info.cid);
        this.info = info;
        eventSocket.connect(token, info.cid);
    }

    /** Update session info without changing the token. Used for identity switching. */
    updateInfo(info: SessionInfo): void {
        this.info = info;
    }

    /** End the current session. */
    clear(): void {
        eventSocket.disconnect();
        api.clearSession();
        clearActiveCid();
        this.info = null;
    }

    /** Whether a session is active. */
    isActive(): boolean {
        return api.isAuthenticated();
    }

    /** Get the current session info. */
    getInfo(): SessionInfo | null {
        return this.info;
    }

    /** Get the current CID. */
    getCid(): string | null {
        return this.info?.cid ?? null;
    }

    /** Get the current session's public handle (the `sid`), or null. */
    getSessionId(): string | null {
        return this.info?.sessionId ?? null;
    }

    /** Get the current account type. */
    getAccountType(): string | null {
        return this.info?.accountType ?? null;
    }

    /**
     * Whether this session holds operator powers — i.e. is an owner/admin
     * delegate of an organization with an active CPR operator↔org binding, or
     * that organization's own account session (operator-org-binding plan §2.7).
     * A personal or agent session never qualifies.
     *
     * The server DERIVES this per request; there is no stored account flag. The
     * value here is the snapshot taken at login and refreshed by
     * `/api/account/me`, so it can go stale within a session: releasing the
     * binding or demoting the delegate revokes the powers immediately while
     * this still reads true. Safe for UI affordances (the server re-derives and
     * refuses), never a substitute for the server's answer — see
     * `operatorAccessRevokedMessage` for the resulting 403.
     */
    isOperator(): boolean {
        return this.info?.isOperator === true;
    }
}

export const session = new SessionState();
